import React, { useState, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext({
  isLoggedIn: false,
  user: null,
  login: () => {},
  logout: () => {},
  setUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  let navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 컴포넌트 마운트 시 서버 세션 확인 (SSO 세션 체크)
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await fetch('/auth/check-session', { credentials: 'include' });
        const data = response.ok ? await response.json() : {};
        if (!data.success || !data.user) throw new Error('No verified session');
        setIsLoggedIn(true); setUser(data.user);
        sessionStorage.setItem('user', JSON.stringify(data.user)); localStorage.setItem('user', JSON.stringify(data.user));
      } catch (_) {
        sessionStorage.removeItem('user'); localStorage.removeItem('user');
        setIsLoggedIn(false); setUser(null);
      } finally { setLoading(false); }
    };
    checkLoginStatus();
  }, []);
  
  // 페이지 포커스 시 세션 재확인
  useEffect(() => {
    const handleFocus = async () => {
      if (isLoggedIn && user) {
        try {
          const response = await fetch('/auth/verify-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ userId: user.id })
          });
          
          if (response.ok) {
            const result = await response.json();
            if (!result.success) {
              // 세션이 만료되었으면 로그아웃 처리
              logout();
            }
          }
        } catch (error) {
          console.log('세션 재확인 실패');
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    // 주기적으로 세션 확인 (5분마다)
    const intervalId = setInterval(handleFocus, 5 * 60 * 1000);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, [isLoggedIn, user]);

  const login = async (userData) => {
    // 두 저장소에 모두 저장
    localStorage.setItem("user", JSON.stringify(userData));
    sessionStorage.setItem("user", JSON.stringify(userData));
    setIsLoggedIn(true);
    setUser(userData);
  };
  
  const logout = async () => {
    const response = await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    if (!response.ok) throw new Error('로그아웃에 실패했습니다. 다시 시도해 주세요.');
    // 두 저장소에서 모두 제거
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    setIsLoggedIn(false);
    setUser(null);
    navigate("/");
  };

  const updateUserProfile = async (profileData) => {
    try {
      const response = await fetch('/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(profileData),
      });

      const result = await response.json();
      
      if (result.success) {
        // 사용자 정보 업데이트
        const updatedUser = { ...user, ...result.user };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
      }

      return result;
    } catch (error) {
      console.error('프로필 업데이트 오류:', error);
      return {
        success: false,
        message: '프로필 업데이트 중 오류가 발생했습니다.'
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, user, login, logout, updateUserProfile, setIsLoggedIn, setUser, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};